import {
  ChevronDown,
  CircleGauge,
  CloudCog,
  DatabaseZap,
  FileStack,
  HeartPulse,
  Image,
  LayoutDashboard,
  Music2,
  LogOut,
  Menu,
  MonitorPlay,
  Newspaper,
  PanelsTopLeft,
  ScrollText,
  Settings,
  ShieldCheck,
  Tv,
  Users,
  UtensilsCrossed,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { api, type AccessMode, type Session } from "./api.js";
import {
  AdminClientsPage,
  AdminAudioLibrariesPage,
  AdminSlideTypesPage,
  AssetsPage,
  OverviewPage,
  PlaylistsPage,
  TvsPage,
} from "./pages.js";
import {
  AdminAuditPage,
  AdminCachePage,
  AdminHealthPage,
  AdminRssPage,
  AdminSettingsPage,
  type PlatformBranding,
} from "./adminOperations.js";
import { AdminAccountPage, ClientAccountPage } from "./clientOperations.js";
import { DeviceCloudPage } from "./DeviceCloudPage.js";
import { MenuWorkspace } from "./MenuWorkspace.js";
import { useModalFocus } from "./useModalFocus.js";

interface DashboardProps {
  session: Session;
  platformBranding: PlatformBranding;
  onSessionChange(session: Session | null): void;
}

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  group: string;
}

interface ClientBranding {
  name: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
}

const adminNav: NavItem[] = [
  { path: "", label: "Overview", icon: LayoutDashboard, group: "Main menu" },
  { path: "clients", label: "Clients", icon: Users, group: "Main menu" },
  { path: "tvs", label: "All TVs", icon: Tv, group: "Main menu" },
  { path: "device-cloud", label: "Device Cloud", icon: CloudCog, group: "Main menu" },
  { path: "slide-types", label: "Slide types", icon: PanelsTopLeft, group: "Content" },
  { path: "rss", label: "RSS sources", icon: Newspaper, group: "Content" },
  { path: "assets", label: "Assets", icon: Image, group: "Content" },
  { path: "audio-lists", label: "Audio lists", icon: Music2, group: "Content" },
  { path: "cache", label: "Cache management", icon: DatabaseZap, group: "Operations" },
  { path: "audit", label: "Audit log", icon: ScrollText, group: "Operations" },
  { path: "health", label: "System health", icon: HeartPulse, group: "Operations" },
  { path: "settings", label: "Platform settings", icon: Settings, group: "Settings" },
  { path: "account", label: "Account security", icon: ShieldCheck, group: "Settings" },
];

const clientNav: NavItem[] = [
  { path: "", label: "Overview", icon: LayoutDashboard, group: "Workspace" },
  { path: "content", label: "Content", icon: FileStack, group: "Workspace" },
  { path: "menu", label: "Menu", icon: UtensilsCrossed, group: "Workspace" },
  { path: "tvs", label: "TVs & activation", icon: Tv, group: "Workspace" },
  { path: "assets", label: "Assets", icon: Image, group: "Workspace" },
  { path: "account", label: "Account security", icon: ShieldCheck, group: "Workspace" },
];

const routeTitles: Record<string, string> = {
  clients: "Clients",
  tvs: "Television displays",
  "device-cloud": "Q-Assist Device Cloud",
  "slide-types": "Slide types",
  content: "Content",
  menu: "Restaurant menu",
  rss: "RSS sources",
  assets: "Asset library",
  "audio-lists": "Predefined audio lists",
  cache: "Cache controls",
  audit: "Audit log",
  health: "System health",
  settings: "Settings",
  account: "Account security",
};

export function Dashboard({ session, platformBranding, onSessionChange }: DashboardProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useModalFocus(sidebarOpen, () => setSidebarOpen(false));
  const location = useLocation();
  const mode: AccessMode = session.user.mode;
  const brandingQuery = useQuery({
    queryKey: ["client", "branding"],
    queryFn: () => api<ClientBranding>("/api/v1/client/branding", {}, session.token),
    enabled: mode === "client",
    staleTime: 15_000,
    refetchInterval: mode === "client" ? 60_000 : false,
    refetchOnWindowFocus: true,
  });
  const branding = mode === "client" ? brandingQuery.data : platformBranding;
  const activeBranding = branding ?? platformBranding;
  const dashboardStyle = {
    "--brand-primary": platformBranding.primaryColor,
    "--brand-secondary": platformBranding.secondaryColor,
  } as CSSProperties;
  const nav = mode === "admin" ? adminNav : clientNav;
  const navGroups = nav.reduce<Array<{ label: string; items: NavItem[] }>>((groups, item) => {
    const current = groups.at(-1);
    if (current?.label === item.group) current.items.push(item);
    else groups.push({ label: item.group, items: [item] });
    return groups;
  }, []);
  const base = mode === "admin" ? "/admin" : "/client";
  const segment = location.pathname.slice(base.length).split("/").filter(Boolean)[0] ?? "";
  const pageTitle =
    routeTitles[segment] ?? (mode === "admin" ? "Platform overview" : "Workspace overview");
  const healthQuery = useQuery({
    queryKey: ["admin", "sidebar-health"],
    queryFn: () =>
      api<{ status: "healthy" | "degraded"; checkedAt: string }>(
        "/api/v1/admin/system-health",
        {},
        session.token,
      ),
    enabled: mode === "admin",
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (!profileOpen) return;
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setProfileOpen(false);
      profileButtonRef.current?.focus();
    };
    const closeOutside = (event: PointerEvent): void => {
      if (event.target instanceof Node && !profileMenuRef.current?.contains(event.target))
        setProfileOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape, true);
    document.addEventListener("pointerdown", closeOutside, true);
    return () => {
      document.removeEventListener("keydown", closeOnEscape, true);
      document.removeEventListener("pointerdown", closeOutside, true);
    };
  }, [profileOpen]);

  useEffect(() => setProfileOpen(false), [location.pathname]);

  async function logout(): Promise<void> {
    try {
      await api("/api/v1/auth/logout", { method: "POST" }, session.token);
    } finally {
      onSessionChange(null);
    }
  }

  return (
    <div className={`dashboard dashboard--atlas dashboard--${mode}`} style={dashboardStyle}>
      <a className="skip-link" href="#dashboard-main-content">
        Skip to main content
      </a>
      <aside
        ref={sidebarRef}
        id="dashboard-navigation"
        className={`sidebar ${sidebarOpen ? "is-open" : ""}`}
        aria-label="Workspace navigation"
        tabIndex={-1}
      >
        <div className="sidebar-head">
          <NavLink className="brand" to={base} onClick={() => setSidebarOpen(false)}>
            {branding?.logoUrl ? (
              <img className="brand-logo" src={branding.logoUrl} alt="" />
            ) : (
              <span className="brand-mark">
                <MonitorPlay size={22} />
              </span>
            )}
            <span>{branding?.name ?? "Q-Assist TV"}</span>
          </NavLink>
          <button
            className="icon-button mobile-only"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close navigation"
            data-modal-initial-focus
          >
            <X size={20} />
          </button>
        </div>
        <div className="workspace-badge">
          <span className="workspace-icon">
            {mode === "admin" ? <ShieldCheck size={18} /> : <CircleGauge size={18} />}
          </span>
          <div>
            <strong>{mode === "admin" ? "Platform control" : activeBranding.name}</strong>
            <span>{mode === "admin" ? "Administrator" : "Active workspace"}</span>
          </div>
        </div>
        <nav className="sidebar-nav" aria-label="Main navigation">
          {navGroups.map((group) => (
            <div className="sidebar-nav-group" key={group.label}>
              <span className="nav-label">{group.label}</span>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={`${base}${item.path ? `/${item.path}` : ""}`}
                    end={!item.path}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon size={19} />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="sidebar-foot">
          <div className="service-status">
            <span
              className={`pulse-dot ${healthQuery.data?.status === "degraded" ? "pulse-dot--warn" : ""}`}
            />
            <div>
              <strong>
                {mode === "admin"
                  ? healthQuery.data?.status === "degraded"
                    ? "Service degradation detected"
                    : healthQuery.isError
                      ? "Health check unavailable"
                      : "All systems operational"
                  : "Workspace connected"}
              </strong>
              <span>
                {mode === "admin" && healthQuery.data?.checkedAt
                  ? `Checked ${new Date(healthQuery.data.checkedAt).toLocaleTimeString()}`
                  : "Live API session"}
              </span>
            </div>
          </div>
        </div>
      </aside>
      {sidebarOpen ? (
        <button
          className="sidebar-scrim"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close navigation"
          aria-hidden="true"
          tabIndex={-1}
        />
      ) : null}

      <div className="dashboard-main">
        <header className="topbar">
          <button
            className="icon-button mobile-only"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
            aria-controls="dashboard-navigation"
            aria-expanded={sidebarOpen}
          >
            <Menu size={21} />
          </button>
          <div className="topbar-title">
            <span>
              {mode === "admin" ? `${platformBranding.name} platform` : activeBranding.name}
            </span>
            <h1>{pageTitle}</h1>
          </div>
          <div className="topbar-actions">
            <div className="profile-menu" ref={profileMenuRef}>
              <button
                ref={profileButtonRef}
                className="profile-button"
                onClick={() => setProfileOpen((value) => !value)}
                aria-expanded={profileOpen}
                aria-controls="profile-account-actions"
                aria-label={`Account menu for ${session.user.name}`}
              >
                <span className="avatar">
                  {session.user.name
                    .split(" ")
                    .map((word) => word[0])
                    .join("")
                    .slice(0, 2)}
                </span>
                <span className="profile-copy">
                  <strong>{session.user.name}</strong>
                  <small>{session.user.email}</small>
                </span>
                <ChevronDown size={16} />
              </button>
              {profileOpen ? (
                <div className="profile-dropdown" id="profile-account-actions">
                  <button onClick={() => void logout()}>
                    <LogOut size={17} /> Sign out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <main id="dashboard-main-content" className="content" tabIndex={-1}>
          {session.impersonation && session.returnSession ? (
            <section className="impersonation-banner" role="status">
              <ShieldCheck size={18} />
              <span>
                Administrator view of <strong>{session.impersonation.clientName}</strong>
              </span>
              <button
                type="button"
                className="button button--secondary"
                onClick={() => {
                  onSessionChange(session.returnSession ?? null);
                  window.location.assign("/admin/clients");
                }}
              >
                Return to admin
              </button>
            </section>
          ) : null}
          <Routes>
            <Route index element={<OverviewPage mode={mode} />} />
            {mode === "admin" ? (
              <>
                <Route path="clients" element={<AdminClientsPage />} />
                <Route path="slide-types" element={<AdminSlideTypesPage />} />
                <Route
                  path="tvs"
                  element={<TvsPage mode="admin" playerUrl={platformBranding.playerUrl} />}
                />
                <Route path="device-cloud" element={<DeviceCloudPage />} />
                <Route path="assets" element={<AssetsPage mode="admin" />} />
                <Route path="audio-lists" element={<AdminAudioLibrariesPage />} />
                <Route path="rss" element={<AdminRssPage />} />
                <Route path="cache" element={<AdminCachePage />} />
                <Route path="audit" element={<AdminAuditPage />} />
                <Route path="health" element={<AdminHealthPage />} />
                <Route
                  path="settings"
                  element={
                    <AdminSettingsPage
                      branding={platformBranding}
                      onFactoryReset={() => onSessionChange(null)}
                    />
                  }
                />
                <Route path="account" element={<AdminAccountPage />} />
              </>
            ) : (
              <>
                <Route path="content" element={<PlaylistsPage />} />
                <Route path="menu" element={<MenuWorkspace />} />
                <Route path="slides" element={<Navigate to="/client/content" replace />} />
                <Route path="playlists" element={<Navigate to="/client/content" replace />} />
                <Route
                  path="tvs"
                  element={<TvsPage mode="client" playerUrl={platformBranding.playerUrl} />}
                />
                <Route path="assets" element={<AssetsPage />} />
                <Route path="account" element={<ClientAccountPage />} />
              </>
            )}
            <Route path="*" element={<Navigate to={base} replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
